import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useLocalStorage from 'hooks/useLocalStorage';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import {
	faChartBar,
	faChartColumn,
	faChartLine,
	faChartPie,
	faCheck,
	faGripDotsVertical,
	faLayerGroup,
	faPlusCircle,
	faQuestion,
	faSave,
	faTable
} from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/ManageWidgetsModal.styled';

export const WIDGET_TYPES = {
	bar: 'bar',
	column: 'column',
	line: 'line',
	pie: 'pie',
	table: 'table',
	cards: 'cards'
};

export const WIDGET_SIZES = {
	small: 'small',
	medium: 'medium',
	large: 'large'
};

// pass onWidgetsOrderChange and isMobile props, when resizing feature is added
const WidgetContent = ({ widget, onCheck, checked }) => {
	const renderPreviewImage = () => {
		switch (widget.type) {
			case WIDGET_TYPES.pie:
				return (
					<S.WidgetPreview backgroundColor={'#60A4EA'}>
						<S.ChartPreviewIcon icon={faChartPie} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			case WIDGET_TYPES.bar:
				return (
					<S.WidgetPreview backgroundColor={'#88C58F'}>
						<S.ChartPreviewIcon icon={faChartBar} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			case WIDGET_TYPES.line:
				return (
					<S.WidgetPreview backgroundColor={'#EA995F'}>
						<S.ChartPreviewIcon icon={faChartLine} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			case WIDGET_TYPES.column:
				return (
					<S.WidgetPreview backgroundColor={'#F58FA9'}>
						<S.ChartPreviewIcon icon={faChartColumn} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			case WIDGET_TYPES.table:
				return (
					<S.WidgetPreview backgroundColor={'#958EF4'}>
						<S.ChartPreviewIcon icon={faTable} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			case WIDGET_TYPES.cards:
				return (
					<S.WidgetPreview backgroundColor={'#ECC37B'}>
						<S.ChartPreviewIcon icon={faLayerGroup} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
			default:
				return (
					<S.WidgetPreview>
						<S.ChartPreviewIcon icon={faQuestion} size="large" color={'unset'} />
					</S.WidgetPreview>
				);
		}
	};
	// const calculateIcon = (size) => {
	// 	switch (size) {
	// 		case 'small':
	// 			return faS;
	// 		case 'medium':
	// 			return faM;
	// 		case 'large':
	// 			return faL;
	// 		default:
	// 			return faS;
	// 	}
	// };

	// const handleResize = (size) => {
	// 	onWidgetsOrderChange((prevState) => {
	// 		const tempState = structuredClone(prevState);
	// 		const widgetIndex = tempState.findIndex((prevStateWidget) => prevStateWidget.id === widget.id);
	// 		tempState[widgetIndex].size = size;
	// 		return tempState;
	// 	});
	// };

	return (
		<>
			{renderPreviewImage()}
			<S.TextContainer>
				<Typography variant="p1">{widget.name}</Typography>
				<Typography variant="p3" color="#696969">
					{widget.infoMessage}
				</Typography>
				{/* {!isMobile && (
					<div style={{ display: 'flex', flexDirection: 'row', marginTop: '6px' }}>
						{['small', 'medium', 'large'].map((size) => (
							<Button
								style={{ marginRight: '6px' }}
								key={size}
								icon={calculateIcon(size)}
								variant={widget.size === size ? 'blue' : 'outline'}
								onClick={() => handleResize(size)}
							/>
						))}
					</div>
				)} */}
			</S.TextContainer>
			<Button
				style={{ width: '5rem', flexShrink: 0 }}
				icon={checked ? faCheck : faPlusCircle}
				onClick={onCheck}
				variant={checked ? 'blue' : 'outline'}
			/>
		</>
	);
};

const DraggableWidgetPreview = ({ widget, onCheck, checked, onWidgetsOrderChange, index }) => {
	const id = widget.accessor;
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: 'widget',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId()
			};
		},
		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			if (dragIndex === hoverIndex) {
				return;
			}
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			onWidgetsOrderChange((prevState) => {
				const tempState = structuredClone(prevState);
				tempState.splice(dragIndex, 1);
				tempState.splice(hoverIndex, 0, prevState[dragIndex]);
				const updatedState = tempState.map((item, index) => ({ ...item, order: index }));
				return updatedState;
			});
			item.index = hoverIndex;
		}
	});

	const [{ isDragging }, drag] = useDrag({
		type: 'widget',
		item: () => ({ id, index }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	});
	const opacity = isDragging ? 0 : 1;
	drag(drop(ref));

	return (
		<S.WidgetContainer ref={ref} data-handler-id={handlerId} style={{ opacity }}>
			<Icon icon={faGripDotsVertical} size="large" style={{ cursor: 'move' }} color={'#B9B9B9'} />
			<WidgetContent
				widget={widget}
				onCheck={onCheck}
				checked={checked}
				onWidgetsOrderChange={onWidgetsOrderChange}
			/>
		</S.WidgetContainer>
	);
};

const WidgetsSettings = ({ widgetsSet, setWidgetsSet, defaultWidgetSet }) => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.tablet);
	const [searchValue, setSearchValue] = useState(null);
	const [selectedFilters, setSelectedFilters] = useState([]);
	const [filteredItems, setFilteredItems] = useState(widgetsSet);

	const filters = [
		{
			type: 'line',
			icon: faChartLine
		},
		{
			type: 'pie',
			icon: faChartPie
		},
		{
			type: 'bar',
			icon: faChartBar
		},
		{
			type: 'column',
			icon: faChartColumn
		},
		{
			type: 'cards',
			icon: faLayerGroup
		},
		{
			type: 'table',
			icon: faTable
		}
	];

	useEffect(() => {
		let newFilteredItems = [...widgetsSet];
		if (searchValue) {
			newFilteredItems = newFilteredItems.filter((w) =>
				t(w.name).toLowerCase().includes(searchValue.toLowerCase())
			);
		}
		if (selectedFilters.length > 0) {
			newFilteredItems = selectedFilters
				.map((filter) => newFilteredItems.filter((w) => w.type === filter))
				.flat();
		}
		setFilteredItems([...newFilteredItems]);
	}, [searchValue, selectedFilters, widgetsSet]);

	const handleCheck = (widget) => {
		const newWidgetsSet = widgetsSet.map((widgetSetItem) =>
			widgetSetItem.id === widget.id ? { ...widgetSetItem, isVisible: !widgetSetItem.isVisible } : widgetSetItem
		);
		setWidgetsSet(newWidgetsSet);
	};

	return (
		<S.SettingsContainer>
			<div>
				<S.SearchWrapper>
					<div>
						<SearchInput value={searchValue} setValue={setSearchValue} />
					</div>
					<S.FilterWrapper>
						{filters.map((filter) => (
							<S.FilterButton
								key={filter.type}
								icon={filter.icon}
								variant={selectedFilters.some((f) => f === filter.type) ? 'outline' : 'greyOutline'}
								style={{
									marginRight: '12px'
								}}
								onClick={() =>
									setSelectedFilters(
										selectedFilters.includes(filter.type)
											? selectedFilters.filter((selectedFilter) => selectedFilter !== filter.type)
											: [...selectedFilters, filter.type]
									)
								}
							/>
						))}
					</S.FilterWrapper>
				</S.SearchWrapper>
				<div>
					<S.Divider />
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}>
						<Typography variant="p3" style={{ marginBottom: '1rem' }}>
							{`${filteredItems.length} ${t('widgetsModal.widgetsFound')}`}
						</Typography>
						<Button
							label={'widgetsModal.reset'}
							variant="blueBorderless"
							disabled={JSON.stringify(widgetsSet) === JSON.stringify(defaultWidgetSet)}
							onClick={() => {
								setWidgetsSet(defaultWidgetSet);
								setFilteredItems(defaultWidgetSet);
							}}
						/>
					</div>
				</div>
			</div>
			<div>
				{!isMobile ? (
					<DndProvider backend={HTML5Backend}>
						{filteredItems.map((widget, index) => (
							<Authorization path={widget.authorizationPath} key={widget.id}>
								<DraggableWidgetPreview
									widget={widget}
									key={widget.id}
									checked={widget.isVisible}
									onCheck={() => handleCheck(widget)}
									onWidgetsOrderChange={setWidgetsSet}
									index={index}
									isMobile={isMobile}
								/>
							</Authorization>
						))}
					</DndProvider>
				) : (
					filteredItems.map((widget) => (
						<Authorization path={widget.authorizationPath} key={widget.id}>
							<S.WidgetContainer key={widget.id}>
								<WidgetContent
									widget={widget}
									checked={widget.isVisible}
									onCheck={() => handleCheck(widget)}
									onWidgetsOrderChange={setWidgetsSet}
									isMobile={isMobile}
								/>
							</S.WidgetContainer>
						</Authorization>
					))
				)}
			</div>
		</S.SettingsContainer>
	);
};

const ManageWidgetsModal = NiceModal.create(({ data }) => {
	const modal = useModal();
	const { pathname } = useLocation();
	const defaultWidgetSet = data[`${pathname}_new`];

	const [widgetsSetStore, setWidgetsSetStore] = useLocalStorage(`${pathname}_new`, defaultWidgetSet);
	const [widgetsSet, setWidgetsSet] = useState(widgetsSetStore);

	const isAuthorized = useAccessControl();

	useEffect(() => {
		const authorizedWidgets = widgetsSet.filter((widget) => isAuthorized(widget.authorizationPath));
		setWidgetsSet([...authorizedWidgets]);
	}, []);

	const handleSave = () => {
		if (widgetsSet !== defaultWidgetSet) {
			setWidgetsSetStore(widgetsSet);
		} else {
			localStorage.removeItem(`${pathname}_new`);
		}
		window.dispatchEvent(new Event('updateWidgetStore'));
		window.dispatchEvent(new Event('updateWidgetVisibility'));
		modal.remove();
	};

	return (
		<Modal position="right" fullwidth maxWidth="x-small" closeOnClickOutside>
			<ModalTitle>
				<Typography variant="p1">{'widgetsModal.title'}</Typography>
			</ModalTitle>
			<ModalContent style={{ width: '100%', height: '100%' }}>
				<WidgetsSettings
					widgetsSet={widgetsSet}
					setWidgetsSet={setWidgetsSet}
					defaultWidgetSet={defaultWidgetSet}
				/>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={() => modal.remove()} />
				<Button
					disabled={JSON.stringify(widgetsSet) === JSON.stringify(widgetsSetStore)}
					icon={faSave}
					label={'save'}
					variant="blue"
					onClick={handleSave}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ManageWidgetsModal;
